import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { PencilIcon, PlusIcon, DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import { fetchMeetingMinutesById, continueMeetingMinutes } from "../../../api/meetingMinutes";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";

export const MeetingMinutesView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [meetingMinutes, setMeetingMinutes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await fetchMeetingMinutesById(id);
        setMeetingMinutes(data);
      } catch (err) {
        console.error("Error fetching meeting minutes:", err);
        setError(true);
        toast.error(t("meeting_minutes.load_error"));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading) {
    return <ResourceLoading />;
  }

  if (error || !meetingMinutes) {
    return <ResourceError />;
  }

  const handleContinueMeeting = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      await continueMeetingMinutes(id, today);
      toast.success(t("meeting_minutes.continue_success"));
      navigate(`/dashboard/meeting-minutes/${id}/edit?continue=true`);
    } catch (err) {
      console.error("Error continuing meeting:", err);
      toast.error(t("meeting_minutes.continue_error"));
    }
  };

  const handleCopyFrom = () => {
    navigate(`/dashboard/meeting-minutes/new?copy_from=${id}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader
        title={`${meetingMinutes.title}${meetingMinutes.project ? ` (${meetingMinutes.project.displayTitle || meetingMinutes.project.name || `Project #${meetingMinutes.project.id}`})` : ""}`}
        actions={[
          {
            label: t("meeting_minutes.edit"),
            path: `/dashboard/meeting-minutes/${id}/edit`,
            icon: PencilIcon,
          },
          ...(meetingMinutes.meeting_type === "project_meeting" ? [{
            label: t("meeting_minutes.continue_meeting"),
            onClick: handleContinueMeeting,
            icon: PlusIcon,
          }] : []),
          {
            label: t("meeting_minutes.templates.copy_from"),
            onClick: handleCopyFrom,
            icon: DocumentDuplicateIcon,
          },
        ]}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4">{meetingMinutes.title}</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">{t("meeting_minutes.meeting_type")}:</span> {meetingMinutes.meeting_type}
              </div>
              {meetingMinutes.project && (
                <div>
                  <span className="font-medium">{t("meeting_minutes.project")}:</span>{" "}
                  <a
                    href={`/dashboard/projects/${meetingMinutes.project.id}`}
                    className="text-indigo-600 hover:text-indigo-800 hover:underline"
                  >
                    {meetingMinutes.project.displayTitle || meetingMinutes.project.name || `Project #${meetingMinutes.project.id}`}
                  </a>
                </div>
              )}
            </div>
            {meetingMinutes.description && (
              <p className="mt-4 text-gray-700">{meetingMinutes.description}</p>
            )}
          </div>

          {meetingMinutes.meetings && meetingMinutes.meetings.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">{t("meeting_minutes.meetings")}</h3>
                <button
                  onClick={() => navigate(`/dashboard/meeting-minutes/${id}/meetings/new`)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  {t("meeting_minutes.add_meeting")}
                </button>
              </div>
              <div className="space-y-3">
                {meetingMinutes.meetings.map((meeting) => (
                  <div
                    key={meeting.id}
                    className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Link
                      to={`/dashboard/meeting-minutes/${id}/meetings/${meeting.id}`}
                      className="flex items-center justify-between"
                    >
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg">
                          {t("meeting_minutes.meeting_number")} {meeting.meeting_number}
                        </h4>
                        <p className="text-sm text-gray-500 mt-1">{meeting.meeting_date}</p>
                        {meeting.groups && meeting.groups.length > 0 && (
                          <p className="text-sm text-gray-600 mt-2">
                            {meeting.groups.length} {t("meeting_minutes.points")}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {meeting.is_finished && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {t("meeting_minutes.meeting.finished")}
                          </span>
                        )}
                        {!meeting.is_finished && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate(`/dashboard/meeting-minutes/${id}/meetings/${meeting.id}`);
                            }}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-xs sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 transition-colors"
                          >
                            <PencilIcon className="h-4 w-4 mr-1.5" />
                            {t("Edit")}
                          </button>
                        )}
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(!meetingMinutes.meetings || meetingMinutes.meetings.length === 0) && (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">{t("meeting_minutes.no_meetings")}</p>
              <button
                onClick={() => navigate(`/dashboard/meeting-minutes/${id}/meetings/new`)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                {t("meeting_minutes.add_first_meeting")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


